type IncludesSection = {
	Entry?: any[];
	Asset?: any[];
};

const isLinkStub = (value: unknown): {linkType: string; id: string} | null => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		return null;
	}

	const sys = (value as Record<string, any>).sys;

	if (!sys || typeof sys !== 'object' || sys.type !== 'Link') {
		return null;
	}

	const {linkType, id} = sys;

	if (typeof linkType !== 'string' || typeof id !== 'string') {
		return null;
	}

	return {linkType, id};
};

/** Deep copy so each link stub gets its own instance (mutations on one path must not leak). */
const cloneResolvedNode = (value: unknown): unknown => {
	if (value === null || value === undefined || typeof value !== 'object') {
		return value;
	}

	try {
		return structuredClone(value);
	} catch {
		return value;
	}
};

const resolveEntryLink = (
	id: string,
	stub: unknown,
	entryMap: Map<string, any>,
	assetMap: Map<string, any>,
	resolvedEntryCache: Map<string, unknown>,
	resolvingEntries: Set<string>,
): unknown => {
	if (resolvedEntryCache.has(id)) {
		return cloneResolvedNode(resolvedEntryCache.get(id));
	}

	if (resolvingEntries.has(id)) {
		const cyclicEntry = entryMap.get(id);

		return cyclicEntry ? cloneResolvedNode(cyclicEntry) : stub;
	}

	const entry = entryMap.get(id);

	if (!entry) {
		return stub;
	}

	resolvingEntries.add(id);

	const resolved = resolveNode(entry, entryMap, assetMap, resolvedEntryCache, resolvingEntries);

	resolvingEntries.delete(id);
	resolvedEntryCache.set(id, resolved);

	return resolved;
};

const resolveNode = (
	node: unknown,
	entryMap: Map<string, any>,
	assetMap: Map<string, any>,
	resolvedEntryCache: Map<string, unknown>,
	resolvingEntries: Set<string>,
): unknown => {
	if (node === null || node === undefined || typeof node !== 'object') {
		return node;
	}

	if (Array.isArray(node)) {
		const result: unknown[] = [];

		for (const item of node) {
			const resolved = resolveNode(item, entryMap, assetMap, resolvedEntryCache, resolvingEntries);

			if (resolved !== undefined) {
				result.push(resolved);
			}
		}

		return result;
	}

	const link = isLinkStub(node);

	if (link) {
		if (link.linkType === 'Entry') {
			return resolveEntryLink(
				link.id,
				node,
				entryMap,
				assetMap,
				resolvedEntryCache,
				resolvingEntries,
			);
		}

		if (link.linkType === 'Asset') {
			return assetMap.get(link.id) ?? node;
		}

		return node;
	}

	const record = node as Record<string, any>;
	const isCollection =
		typeof record.__typename === 'string' && record.__typename.endsWith('Collection');
	const result: Record<string, any> = {};

	for (const [key, value] of Object.entries(record)) {
		if (key === 'items' && Array.isArray(value)) {
			const resolved = resolveNode(value, entryMap, assetMap, resolvedEntryCache, resolvingEntries) as unknown[];
			result[key] = resolved;

			if ('total' in record) {
				result.total = resolved.length;
			}
		} else if (key === 'items' && isCollection && value && typeof value === 'object' && !Array.isArray(value)) {
			const localeMap = value as Record<string, unknown>;
			const resolvedMap: Record<string, unknown> = {};

			for (const [loc, locValue] of Object.entries(localeMap)) {
				resolvedMap[loc] = resolveNode(locValue, entryMap, assetMap, resolvedEntryCache, resolvingEntries);
			}
			result[key] = resolvedMap;
		} else {
			result[key] = resolveNode(value, entryMap, assetMap, resolvedEntryCache, resolvingEntries);
		}
	}

	return result;
};

/**
 * Resolves a v2 API response that uses deduplicated `includes` by replacing
 * link stubs in `items` with full entities from the `includes` section.
 * Mutates the response in-place: after this call, `items` are fully expanded
 * (same shape as inline-resolved responses) and `includes` is removed.
 *
 * Resolved entries are cached by id for cycle-safe first resolution; each
 * subsequent link stub to the same entry receives a deep clone so in-place
 * mutations (e.g. navigation parent) on one branch do not affect another.
 */
export function resolveV2Includes(response: {items: any[]; includes?: IncludesSection; [key: string]: any}): void {
	const {includes} = response;

	if (!includes) {
		return;
	}

	const entryMap = new Map<string, any>();
	const assetMap = new Map<string, any>();

	if (includes.Entry) {
		for (const entry of includes.Entry) {
			if (entry?.sys?.id) {
				entryMap.set(entry.sys.id, entry);
			}
		}
	}

	if (includes.Asset) {
		for (const asset of includes.Asset) {
			if (asset?.sys?.id) {
				assetMap.set(asset.sys.id, asset);
			}
		}
	}

	for (const item of response.items) {
		if (item?.sys?.id && !entryMap.has(item.sys.id)) {
			entryMap.set(item.sys.id, item);
		}
	}

	const resolvedEntryCache = new Map<string, unknown>();
	const resolvedItems: any[] = [];

	for (const item of response.items) {
		const id = item?.sys?.id;
		const resolvingEntries = new Set<string>();

		if (typeof id === 'string') {
			resolvingEntries.add(id);
		}

		resolvedItems.push(resolveNode(item, entryMap, assetMap, resolvedEntryCache, resolvingEntries));
	}

	response.items = resolvedItems;
	delete response.includes;
}
